import { useState, useEffect, useCallback } from 'react';

export interface TabItem {
  path: string;
  label: string;
  icon?: React.ComponentType<Record<string, unknown>>;
}

interface UseDraggableTabsProps {
  defaultTabs: TabItem[];
  storageKey: string;
}

interface UseDraggableTabsReturn {
  tabs: TabItem[];
  draggedIndex: number | null;
  dragOverIndex: number | null;
  handleDragStart: (index: number) => void;
  handleDragEnter: (index: number) => void;
  handleDragOver: (e: React.DragEvent) => void;
  handleDragLeave: () => void;
  handleDrop: (index: number) => void;
  handleDragEnd: () => void;
  resetTabs: () => void;
  getFirstTabPath: () => string;
}

export const useDraggableTabs = ({
  defaultTabs,
  storageKey,
}: UseDraggableTabsProps): UseDraggableTabsReturn => {
  const [tabs, setTabs] = useState<TabItem[]>(() => {
    const saved = localStorage.getItem(storageKey);
    if (saved) {
      try {
        const savedPaths = JSON.parse(saved) as string[];
        if (Array.isArray(savedPaths) && savedPaths.length > 0) {
          return savedPaths
            .map(path => defaultTabs.find(tab => tab.path === path))
            .filter(Boolean) as TabItem[];
        }
      } catch (error) {
        console.error('Error parsing saved tabs:', error);
      }
    }
    return defaultTabs;
  });

  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);

  // Sauvegarder l'ordre des onglets
  useEffect(() => {
    const paths = tabs.map(tab => tab.path);
    localStorage.setItem(storageKey, JSON.stringify(paths));
  }, [tabs, storageKey]);

  const handleDragStart = useCallback((index: number) => {
    setDraggedIndex(index);
  }, []);

  const handleDragEnter = useCallback((index: number) => {
    setDragOverIndex(index);
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
  }, []);

  const handleDragLeave = useCallback(() => {
    setDragOverIndex(null);
  }, []);

  const handleDrop = useCallback((index: number) => {
    if (draggedIndex === null || draggedIndex === index) {
      setDraggedIndex(null);
      setDragOverIndex(null);
      return;
    }

    const newTabs = [...tabs];
    const draggedTab = newTabs[draggedIndex];
    newTabs.splice(draggedIndex, 1);
    newTabs.splice(index, 0, draggedTab);
    
    setTabs(newTabs);
    setDraggedIndex(null);
    setDragOverIndex(null);
  }, [draggedIndex, tabs]);

  const handleDragEnd = useCallback(() => {
    setDraggedIndex(null);
    setDragOverIndex(null);
  }, []);

  const resetTabs = useCallback(() => {
    setTabs(defaultTabs);
    localStorage.removeItem(storageKey);
  }, [defaultTabs, storageKey]);

  const getFirstTabPath = useCallback(() => {
    return tabs[0]?.path || defaultTabs[0]?.path || '';
  }, [tabs, defaultTabs]);

  return {
    tabs,
    draggedIndex,
    dragOverIndex,
    handleDragStart,
    handleDragEnter,
    handleDragOver,
    handleDragLeave,
    handleDrop,
    handleDragEnd,
    resetTabs,
    getFirstTabPath,
  };
};
